import { getSpellTemplate } from "@vinyl/shared";
import { el } from "./dom.js";

// Fixed bottom-center cast bar (sits just above the hotbar). T4C-faithful:
// shows the in-progress cast or auto-cast cycle, always visible while a cast
// is active. Driven by castStateUpdate from the server: castEndsAt (absolute
// serverTime when the spell resolves) + castDurationMs (the wind-up window).
//
// Server time vs local time: the client tracks the server-time offset via
// world snapshots (already wired). Pass `nowServer` in tick() to avoid clock
// skew.
export class CastBar {
  readonly root: HTMLElement;
  private fill: HTMLElement;
  private label: HTMLElement;
  private spellId: string | null = null;
  private castEndsAt: number | null = null;
  private castDurationMs = 0;

  constructor() {
    this.root = el("div", { class: "cast-bar hidden" });
    this.label = el("div", { class: "cast-bar-label" }, "");
    this.fill = el("div", { class: "cast-bar-fill" });
    const track = el("div", { class: "cast-bar-track" }, this.fill);
    this.root.append(this.label, track);
  }

  setState(spellId: string | null, castEndsAt: number | null, castDurationMs: number): void {
    this.spellId = spellId;
    this.castEndsAt = castEndsAt;
    this.castDurationMs = castDurationMs;
    if (!spellId) this.root.classList.add("hidden");
  }

  tick(nowServer: number): void {
    if (!this.spellId || this.castEndsAt === null) {
      this.root.classList.add("hidden");
      return;
    }
    // Visible window: [castEndsAt - castDurationMs, castEndsAt]. Outside that
    // we're in post-resolution cooldown (no bar shown).
    const windUpStart = this.castEndsAt - this.castDurationMs;
    if (nowServer < windUpStart || nowServer > this.castEndsAt) {
      this.root.classList.add("hidden");
      return;
    }
    const total = Math.max(1, this.castDurationMs);
    const progress = Math.max(0, Math.min(1, (nowServer - windUpStart) / total));
    this.root.classList.remove("hidden");
    this.fill.style.width = `${progress * 100}%`;
    const tpl = getSpellTemplate(this.spellId);
    this.label.textContent = tpl ? `Casting ${tpl.name}…` : "Casting…";
  }

  destroy(): void {
    this.root.remove();
  }
}
